// Time Report Service - Database Operations
import { supabase } from '../supabaseClient';
import { TimeLog, StaffMember } from '../../types/timeReport';

/**
 * Fetch all active staff members
 */
export const fetchAllStaff = async (): Promise<StaffMember[]> => {
  const { data, error } = await supabase
    .from('team')
    .select('id, name, role, photo, is_active')
    .eq('is_active', true)
    .order('name', { ascending: true });

  if (error) {
    throw new Error(`Error fetching staff: ${error.message}`);
  }

  return data || [];
};

/**
 * Fetch time logs for a date range and optional staff filter
 * Uses the crew_time_reports view which includes calculated fields
 */
export const fetchTimeLogs = async (
  startDate: Date,
  endDate: Date,
  staffId?: string | null
): Promise<TimeLog[]> => {
  let query = supabase
    .from('crew_time_reports')
    .select('*')
    .gte('clock_in_time', startDate.toISOString())
    .lte('clock_in_time', endDate.toISOString());

  if (staffId) {
    query = query.eq('staff_id', staffId);
  }

  const { data, error } = await query.order('clock_in_time', { ascending: true });

  if (error) {
    throw new Error(`Error fetching time logs: ${error.message}`);
  }

  return data || [];
};

/**
 * Fetch incomplete shifts (missing clock out time)
 */
export const fetchIncompleteShifts = async (staffId?: string | null): Promise<TimeLog[]> => {
  let query = supabase
    .from('crew_time_reports')
    .select('*')
    .is('clock_out_time', null);

  if (staffId) {
    query = query.eq('staff_id', staffId);
  }

  const { data, error } = await query.order('clock_in_time', { ascending: false });

  if (error) {
    throw new Error(`Error fetching incomplete shifts: ${error.message}`);
  }

  return data || [];
};

/**
 * Fetch currently active shifts (clocked in right now)
 */
export const fetchActiveShifts = async (staffId?: string | null): Promise<TimeLog[]> => {
  let query = supabase
    .from('crew_time_reports')
    .select('*')
    .eq('status', 'clocked_in')
    .is('clock_out_time', null);

  if (staffId) {
    query = query.eq('staff_id', staffId);
  }

  const { data, error } = await query.order('clock_in_time', { ascending: false });

  if (error) {
    throw new Error(`Error fetching active shifts: ${error.message}`);
  }

  return data || [];
};

/**
 * Update a time log entry (admin correction)
 */
export const updateTimeLog = async (
  logId: string,
  updates: {
    clock_in_time?: string;
    clock_out_time?: string;
    status?: 'clocked_in' | 'clocked_out';
    admin_notes?: string;
    edited_by: string;
  }
): Promise<TimeLog> => {
  const updateData = {
    ...updates,
    edited_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('crew_time_logs')
    .update(updateData)
    .eq('id', logId)
    .select()
    .single();

  if (error) {
    throw new Error(`Error updating time log: ${error.message}`);
  }

  return data;
};

/**
 * Set a shift to 1 hour (default for incomplete/forgotten shifts)
 */
export const setShiftToDefault = async (
  logId: string,
  clockInTime: string,
  adminUserId: string
): Promise<TimeLog> => {
  const clockIn = new Date(clockInTime);
  const clockOut = new Date(clockIn);
  clockOut.setHours(clockIn.getHours() + 1); // 1 hour default

  return updateTimeLog(logId, {
    clock_out_time: clockOut.toISOString(),
    status: 'clocked_out',
    admin_notes: 'Auto-set to 1 hour by admin (forgot to clock out)',
    edited_by: adminUserId,
  });
};

// Keep old name for backwards compatibility
export const setShiftTo8Hours = setShiftToDefault;

/**
 * Get time log by ID
 */
export const getTimeLogById = async (logId: string): Promise<TimeLog | null> => {
  const { data, error } = await supabase
    .from('crew_time_reports')
    .select('*')
    .eq('id', logId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Not found
    }
    throw new Error(`Error fetching time log: ${error.message}`);
  }

  return data;
};

/**
 * Delete a time log (admin only, use carefully)
 */
export const deleteTimeLog = async (logId: string): Promise<void> => {
  const { error } = await supabase
    .from('crew_time_logs')
    .delete()
    .eq('id', logId);

  if (error) {
    throw new Error(`Error deleting time log: ${error.message}`);
  }
};
